import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { MenuItem } from 'primeng/api';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { SkeletonModule } from 'primeng/skeleton';
import { ChipModule } from 'primeng/chip';
import { DividerModule } from 'primeng/divider';
import { TextareaModule } from 'primeng/textarea';
import { ToastService } from 'src/app/services/toast.service';

interface MySynergy {
  id: number;
  title: string;
  description: string;
  category: string;
  status: 'active' | 'pending' | 'completed' | 'paused' | 'rejected';
  match_score: number;
  smart_coins_earned: number;
  rating?: number;
  partner_hub: any
  my_confirmed: boolean;
  partner_confirmed: boolean;
  potential_benefits: string[];
  ai_reasoning: string;
  created_at: string;
  activated_at?: string;
}

interface TimelineEvent {
  id: number;
  event_type: string;
  title: string;
  description: string;
  created_by_hub_name?: string;
  created_at: string;
}

interface SynergySuggestion {
  id: number;
  candidate_hub_id: number;
  candidate_hub_name: string;
  candidate_hub_city: string;
  candidate_hub_type: string;
  distance_km: number;
  same_regional_hub: boolean;
  match_score: number;
  category: string;
  title: string;
  description: string;
  reasoning: string;
  potential_benefits: string[];
  expected_smart_coins: number;
}

@Component({
  selector: 'app-synergy-detail',
  templateUrl: './synergy-detail.page.html',
  styleUrls: ['./synergy-detail.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    BreadcrumbModule,
    
    DialogModule,
    InputTextModule,
    TextareaModule,
    FormsModule,
    SkeletonModule,
    ChipModule,
    DividerModule
  ],
  providers: [MessageService]
})
export class SynergyDetailPage implements OnInit {
  synergy: MySynergy | null = null;
  suggestion: SynergySuggestion | null = null;
  timeline: TimelineEvent[] = [];
  isExpressingInterest = false;
  isLoading = true;
  breadcrumbItems: MenuItem[] = [];
  join_code: string = '';

  // ✅ Confirm Modal
  showConfirmModal = false;
  partnerJoinCode = '';
  isConfirmingSynergy = false;

  // ✅ NEU: Post Modal
  showPostModal = false;
  postTitle = '';
  postDescription = '';
  isPostingUpdate = false;
  codeCopied = false;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private messageService: MessageService,
    private _toast: ToastService,
    private _http: HttpClient
  ) { }

  ngOnInit() {
    this.loadData()
    this.route.params.subscribe(params => {
      const type = params['type'];
      const id = params['id'];

      if (type === 'my-synergy') {
        this.loadSynergy(id);
      } else if (type === 'suggestion') {
        this.loadSuggestion(id);
      }
    });

    this.setupBreadcrumbs();
  }

  setupBreadcrumbs() {
    this.breadcrumbItems = [
      { label: 'Business Hub', routerLink: '/dashboard' },
      { label: 'Synergien', routerLink: '/dashboard/synergies' },
      { label: 'Details' }
    ];
  }

  async loadSynergy(id: string) {
    try {
      this.isLoading = true;
      const response = await firstValueFrom(
        this.http.get<any>(`${environment.apiUrl}/business/synergies/${id}`)
      );

      // Determine which hub is the partner
      const partnerHub = response.hub_1?.id !== parseInt(id) ? response.hub_1 : response.hub_2;

      this.synergy = {
        id: response.id,
        title: response.title,
        description: response.description,
        category: response.category,
        status: response.status,
        match_score: response.match_score,
        smart_coins_earned: response.smart_coins_earned,
        rating: response.rating,
        partner_hub: partnerHub,
        my_confirmed: response.hub_1_confirmed,
        partner_confirmed: response.hub_2_confirmed,
        potential_benefits: response.potential_benefits || [],
        ai_reasoning: response.ai_reasoning,
        created_at: response.created_at,
        activated_at: response.activated_at
      };

      this.timeline = response.timeline || [];
    } catch (error) {
      console.error('Error loading synergy:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Synergy konnte nicht geladen werden'
      });
      this.router.navigate(['/dashboard/synergies']);
    } finally {
      this.isLoading = false;
    }
  }

  loadSuggestion(id: string) {
    try {
      this.isLoading = true;

      const storedSuggestion = sessionStorage.getItem('current_suggestion');

      if (storedSuggestion) {
        const parsed = JSON.parse(storedSuggestion);

        if (parsed.candidate_hub_id === parseInt(id)) {
          this.suggestion = parsed;
          sessionStorage.removeItem('current_suggestion');
          this.isLoading = false;
          return;
        }
      }

      this.messageService.add({
        severity: 'info',
        summary: 'Info',
        detail: 'Bitte wähle einen Vorschlag aus der Liste'
      });
      this.router.navigate(['/dashboard/synergies'], {
        queryParams: { tab: 'find-synergies' }
      });
    } finally {
      this.isLoading = false;
    }
  }

  onBack() {
    this.router.navigate(['/dashboard/synergies']);
  }

  // ✅ Modal öffnen
  onConfirm() {
    if (!this.synergy) return;
    this.showConfirmModal = true;
  }

  // ✅ Bestätigung
  async confirmSynergy() {
    if (!this.synergy || !this.partnerJoinCode.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Achtung',
        detail: 'Bitte Join-Code des Partners eingeben'
      });
      return;
    }

    try {
      this.isConfirmingSynergy = true;
      const response = await firstValueFrom(
        this.http.post<{ status: string; message: string; synergy_active: boolean }>(
          `${environment.apiUrl}/business/synergies/${this.synergy.id}/confirm`,
          {
            partner_join_code: this.partnerJoinCode.toUpperCase().trim()
          }
        )
      );

      this.messageService.add({
        severity: 'success',
        summary: 'Erfolg',
        detail: response.message
      });

      this.showConfirmModal = false;
      this.partnerJoinCode = '';

      // Reload
      await this.loadSynergy(this.synergy.id.toString());
    } catch (error: any) {
      console.error('Error confirming synergy:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: error?.error?.message || 'Synergy konnte nicht bestätigt werden'
      });
    } finally {
      this.isConfirmingSynergy = false;
    }
  }

  closeConfirmModal() {
    this.showConfirmModal = false;
    this.partnerJoinCode = '';
  }

  async onExpressInterest() {
    if (!this.suggestion) return;

    this.isExpressingInterest = true;

    try {
      await firstValueFrom(
        this.http.post<{ status: string; message: string; synergy_id: number }>(
          `${environment.apiUrl}/business/synergies/express-interest/${this.suggestion.id}`,
          {}
        )
      );

      this.messageService.add({
        severity: 'success',
        summary: 'Erfolg',
        detail: `Dein Interesse an ${this.suggestion.candidate_hub_name} wurde übermittelt.`
      });

      setTimeout(() => {
        this.router.navigate(['/dashboard/synergies']);
      }, 1500);
    } catch (error) {
      console.error('Error expressing interest:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Interesse konnte nicht bekundet werden'
      });
    } finally {
      this.isExpressingInterest = false;
    }
  }

  // ✅ NEU: Post Modal öffnen
  openPostModal() {
    this.showPostModal = true;
    this.postTitle = '';
    this.postDescription = '';
  }

  closePostModal() {
    this.showPostModal = false;
    this.postTitle = '';
    this.postDescription = '';
  }

  // ✅ NEU: Post erstellen
  async submitPost() {
    if (!this.synergy || !this.postTitle.trim() || !this.postDescription.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Achtung',
        detail: 'Bitte Titel und Beschreibung eingeben'
      });
      return;
    }

    try {
      this.isPostingUpdate = true;
      const response = await firstValueFrom(
        this.http.post<{ status: string; message: string; event: TimelineEvent }>(
          `${environment.apiUrl}/business/synergies/${this.synergy.id}/posts`,
          {
            title: this.postTitle.trim(),
            description: this.postDescription.trim()
          }
        )
      );

      this.messageService.add({
        severity: 'success',
        summary: 'Erfolg',
        detail: response.message
      });

      // Timeline neu laden
      this.timeline.unshift(response.event);

      this.closePostModal();
    } catch (error: any) {
      console.error('Error posting update:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: error?.error?.message || 'Update konnte nicht gepostet werden'
      });
    } finally {
      this.isPostingUpdate = false;
    }
  }

  // ✅ FIXED: Telefon & Email Actions
  openTel() {
    console.log(this.synergy?.partner_hub);

    if (this.synergy?.partner_hub?.tel) {
      window.open(`tel:${this.synergy.partner_hub.tel}`, '_self');
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Achtung',
        detail: 'Keine Telefonnummer verfügbar'
      });
    }
  }

  openMail() {
    if (this.synergy?.partner_hub?.email) {
      window.open(`mailto:${this.synergy.partner_hub.email}`, '_self');
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Achtung',
        detail: 'Keine E-Mail verfügbar'
      });
    }
  }

  getStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      'active': 'Aktiv',
      'pending': 'Wartend',
      'completed': 'Abgeschlossen',
      'paused': 'Pausiert',
      'rejected': 'Abgelehnt'
    };
    return labels[status] || status;
  }

  getStatusSeverity(status: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warn';
      case 'completed': return 'info';
      case 'paused': return 'secondary';
      case 'rejected': return 'danger';
      default: return 'secondary';
    }
  }

  getCategoryIcon(category: string): string {
    const icons: { [key: string]: string } = {
      'supply-chain': 'pi pi-truck',
      'energy': 'pi pi-bolt',
      'mobility': 'pi pi-car',
      'innovation': 'pi pi-lightbulb',
      'service': 'pi pi-cog',
      'marketing': 'pi pi-megaphone',
      'resources': 'pi pi-box'
    };
    return icons[category] || 'pi pi-circle';
  }

  getCategoryColor(category: string): string {
    const colors: { [key: string]: string } = {
      'supply-chain': '#3b82f6',
      'energy': '#f59e0b',
      'mobility': '#10b981',
      'innovation': '#8b5cf6',
      'service': '#06b6d4',
      'marketing': '#ec4899',
      'resources': '#6366f1'
    };
    return colors[category] || '#6b7280';
  }

  getEventIcon(eventType: string): string {
    const icons: { [key: string]: string } = {
      'created': 'pi pi-plus-circle',
      'confirmed': 'pi pi-check-circle',
      'activated': 'pi pi-star-fill',
      'note_added': 'pi pi-file-edit',
      'post': 'pi pi-megaphone',
      'status_changed': 'pi pi-refresh'
    };
    return icons[eventType] || 'pi pi-circle';
  }

  getEventColor(eventType: string): string {
    const colors: { [key: string]: string } = {
      'created': '#6b7280',
      'confirmed': '#10b981',
      'activated': '#f59e0b',
      'note_added': '#3b82f6',
      'post': '#8b5cf6',
      'status_changed': '#06b6d4'
    };
    return colors[eventType] || '#6b7280';
  }
  async loadData() {
    try {
      // Load personal data
      const personalData = await firstValueFrom(
        this._http.get<any>(`${environment.apiUrl}/business/users/me`)
      );
      this.join_code = personalData.hub.join_code;

    } catch (error) {
      console.error('Error loading data:', error);

    } finally {

    }
  }
  async copyJoinCode() {
    if (!this.join_code) {
      this._toast.add({
        severity: 'warn',
        summary: 'Achtung',
        detail: 'Kein Join-Code verfügbar'
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(this.join_code);
      this.codeCopied = true;

      this._toast.add({
        severity: 'success',
        summary: 'Kopiert!',
        detail: 'Join-Code wurde in die Zwischenablage kopiert'
      });

      // Reset icon nach 2 Sekunden
      setTimeout(() => {
        this.codeCopied = false;
      }, 2000);
    } catch (err) {
      console.error('Fehler beim Kopieren:', err);
      this._toast.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Join-Code konnte nicht kopiert werden'
      });
    }
  }

}